import { useMemo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from '@tanstack/react-router';
import { Plus, Waypoints } from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { ProcessListRow, ProcessStatusSchema } from '@bliks-io/contracts';
import { useProcesses } from '@/api/queries/useProcesses';
import { useCreateProcess, useUpdateProcess, useDeleteProcess } from '@/api/mutations/useProcesses';
import { useCrudDialogState } from '@/hooks/useCrudDialogState';
import { useCrudHandlers } from '@/hooks/useCrudHandlers';
import {
  DeleteConfirmDialog,
  Button,
  Badge,
  ScoreBadge,
  Page,
  PageHeader,
  PageContent,
  DataTable,
  createColumnHeader,
  renderFallback,
  createTextColumn,
  createDateColumn,
  filterNumberRange,
  EntityActions,
  InfoHint,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { CapacityCell } from '@/components/capacity-cell';
import { ProcessFormDialog } from './components/ProcessFormDialog';
import { DuplicateProcessDialog } from './components/DuplicateProcessDialog';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/usePermissions';
import { ProcessTabsNav } from '@/components/processes/ProcessTabsNav';
import { ContentHeader } from '@/components/content-header';
import {
  PROCESS_CATEGORY_COLOR_SCHEME,
  PROCESS_STATUS_COLOR_SCHEME,
  PROCESS_STATUS_ORDER,
  processStatusLabel
} from '@/lib/process-utils';

export function ProcessesListPage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.allProcesses');
  const slug = useAuthStore().slug();
  const navigate = useNavigate();
  const { data: processes, isLoading } = useProcesses();

  const { can } = usePermissions();
  const canCreateProcess = can('Process', 'create');

  // Both gates read the rows: the rating rides `readProcessInsights` and the
  // capacity binding stayed on `readSensitive` (ADR 0021), so a viewer can hold
  // one without the other.
  const { showRatingColumn, showCapacityColumn } = useMemo(
    () => ({
      showRatingColumn: processes?.some((p) => p.capabilities?.canViewProcessInsights) ?? false,
      showCapacityColumn: processes?.some((p) => p.capabilities?.canViewSensitiveFields) ?? false
    }),
    [processes]
  );

  // Per-process rating access uses backend-computed capabilities
  const hasRatingAccess = useCallback(
    (process: ProcessListRow): boolean => process.capabilities?.canViewProcessInsights ?? false,
    []
  );

  // Mutations
  const createMutation = useCreateProcess();
  const updateMutation = useUpdateProcess();
  const deleteMutation = useDeleteProcess();

  // CRUD dialog state management
  const {
    formDialogOpen,
    editingEntity: editingProcess,
    deleteDialogOpen,
    deletingEntityId: deletingProcessId,
    openEdit: handleEdit,
    openDelete: handleDelete,
    closeFormDialog,
    closeDeleteDialog
  } = useCrudDialogState<ProcessListRow>();

  const [duplicatingProcess, setDuplicatingProcess] = useState<ProcessListRow | null>(null);

  // CRUD handlers with error handling
  const { handleFormSubmit, handleDeleteConfirm, isDeleting } = useCrudHandlers({
    createMutation,
    updateMutation,
    deleteMutation,
    editingEntity: editingProcess,
    deletingEntityId: deletingProcessId,
    t,
    onFormSuccess: closeFormDialog,
    onDeleteSuccess: closeDeleteDialog
  });

  // Handlers
  const handleCreate = () => {
    navigate({ to: '/$slug/processes/create-processes', params: { slug } });
  };

  const handleView = useCallback(
    (process: ProcessListRow) => {
      navigate({
        to: '/$slug/processes/all-processes/$processId',
        params: { slug, processId: process._id }
      });
    },
    [navigate, slug]
  );

  // Define columns with stable reference using useMemo
  const columns = useMemo<DataTableColumnDef<ProcessListRow>[]>(() => {
    const baseColumns: DataTableColumnDef<ProcessListRow>[] = [
      {
        accessorKey: 'name',
        meta: { label: t('common.Name'), filterType: 'text' },
        header: createColumnHeader(t('common.Name')),
        cell: ({ row }) => (
          <Link
            to="/$slug/processes/all-processes/$processId"
            params={{ slug, processId: row.original._id }}
            className="font-medium hover:underline"
          >
            {row.getValue('name')}
          </Link>
        ),
        enableColumnFilter: true
      },
      createTextColumn<ProcessListRow>('description', t('common.Description'), {
        enableFiltering: true
      }),
      // TODO: Review AI Code for feature: Process Map
      {
        accessorKey: 'category',
        meta: { label: t('processes.category') },
        header: createColumnHeader(t('processes.category')),
        cell: ({ row }) => {
          const category = row.original.category;
          if (!category) return <span className="text-muted-foreground">-</span>;
          return (
            <Badge colorScheme={PROCESS_CATEGORY_COLOR_SCHEME[category]}>
              {t(`processMap.lanes.${category}`)}
            </Badge>
          );
        },
        enableColumnFilter: false
      },
      {
        accessorKey: 'department',
        meta: { label: t('processes.department'), filterType: 'text' },
        header: createColumnHeader(t('processes.department')),
        cell: ({ row }) => {
          const department = row.original.department;
          return renderFallback(
            typeof department === 'object' && department ? department.name : department
          );
        },
        accessorFn: (row) => {
          const department = row.department;
          return typeof department === 'object' && department ? department.name : department;
        },
        enableColumnFilter: true
      },
      {
        id: 'status',
        // The accessor stays on the raw enum: TanStack caches accessor values
        // per row, so a translated one would survive a locale switch.
        accessorFn: (row) => row.status,
        meta: {
          label: t('processes.status'),
          filterType: 'select',
          filterOptions: ProcessStatusSchema.options.map((status) => ({
            value: status,
            label: processStatusLabel(status, t)
          }))
        },
        header: createColumnHeader(t('processes.status')),
        cell: ({ row }) => (
          <Badge colorScheme={PROCESS_STATUS_COLOR_SCHEME[row.original.status]}>
            {processStatusLabel(row.original.status, t)}
          </Badge>
        ),
        // Lifecycle order, not the enum's alphabet.
        sortFn: (rowA, rowB) =>
          PROCESS_STATUS_ORDER.indexOf(rowA.original.status) -
          PROCESS_STATUS_ORDER.indexOf(rowB.original.status),
        enableColumnFilter: true,
        // The accessor is the raw enum, so a search for "recording" would match
        // rows whose visible text never contains it.
        enableGlobalFilter: false
      },
      {
        id: 'stepCount',
        meta: { label: t('processes.recordedHeader') },
        header: () => (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold">
            {t('processes.recordedHeader')}
            <InfoHint content={t('processes.recordedTooltip')} />
          </span>
        ),
        cell: ({ row }) => {
          const stats = row.original.stats;
          if (!stats || stats.totalSteps === 0) {
            return <span className="text-muted-foreground">-</span>;
          }
          return (
            <Badge colorScheme="gray" className="font-medium tabular-nums">
              {stats.userInvolvedSteps}/{stats.totalSteps}
            </Badge>
          );
        },
        enableColumnFilter: false
      }
    ];

    if (showRatingColumn) {
      baseColumns.push({
        id: 'averageRating',
        accessorFn: (row) => row.stats?.averageRating ?? -1,
        meta: { label: t('processes.averageRating'), filterType: 'numberRange' },
        header: createColumnHeader(t('processes.averageRating')),
        cell: ({ row }) => {
          if (!hasRatingAccess(row.original)) {
            return <span className="text-muted-foreground">-</span>;
          }
          const rating = row.original.stats?.averageRating ?? null;
          return <ScoreBadge value={rating} variant="boxed" colorScheme="dynamic" />;
        },
        enableColumnFilter: true,
        filterFn: filterNumberRange,
        enableSorting: true,
        sortFn: (rowA, rowB) => {
          const a = rowA.original.stats?.averageRating ?? -1;
          const b = rowB.original.stats?.averageRating ?? -1;
          return a - b;
        }
      });
    }

    if (showCapacityColumn) {
      baseColumns.push({
        id: 'capacityBinding',
        accessorFn: (row) => row.capacityBinding?.hoursPerYear ?? -1,
        meta: { label: t('processes.capacityBinding') },
        header: createColumnHeader(t('processes.capacityBinding')),
        cell: ({ row }) => <CapacityCell capacity={row.original.capacityBinding} />,
        enableColumnFilter: false,
        enableSorting: true,
        sortFn: (rowA, rowB) => {
          const a = rowA.original.capacityBinding?.hoursPerYear ?? -1;
          const b = rowB.original.capacityBinding?.hoursPerYear ?? -1;
          return a - b;
        }
      });
    }

    // Add remaining columns
    baseColumns.push(
      createDateColumn<ProcessListRow>('createdAt', t('common.Created'), {
        enableFiltering: true
      }),
      {
        id: 'actions',
        meta: { sticky: 'right', fitContent: true },
        header: () => <div className="sr-only">{t('common.Actions')}</div>,
        cell: ({ row }) => {
          const canEdit = row.original.capabilities?.canUpdate ?? false;
          const canDeleteRow = row.original.capabilities?.canDelete ?? false;
          return (
            <EntityActions
              onView={() => handleView(row.original)}
              onEdit={canEdit ? () => handleEdit(row.original) : undefined}
              editLabel={t('processes.editDetails')}
              onClone={
                row.original.capabilities?.canDuplicate
                  ? () => setDuplicatingProcess(row.original)
                  : undefined
              }
              onDelete={canDeleteRow ? () => handleDelete(row.original._id) : undefined}
            />
          );
        },
        enableSorting: false,
        enableHiding: false
      }
    );

    return baseColumns;
  }, [
    t,
    slug,
    handleView,
    handleEdit,
    handleDelete,
    hasRatingAccess,
    showRatingColumn,
    showCapacityColumn
  ]);

  return (
    <Page>
      <PageHeader>
        <ProcessTabsNav />
      </PageHeader>
      <PageContent>
        <div className="flex items-center justify-between">
          <ContentHeader
            icon={Waypoints}
            title={t('processes.title')}
            description={t('processes.description')}
          />
          {canCreateProcess && (
            <Button variant="primary" size="medium" onClick={handleCreate}>
              <Plus className="h-4 w-4" />
              {t('processes.createProcess')}
            </Button>
          )}
        </div>

        <div className="mt-8">
          <DataTable
            columns={columns}
            data={processes || []}
            isLoading={isLoading}
            emptyMessage={t('processes.empty')}
            enableGlobalFilter
            globalSearchPlaceholder={t('processes.search', { defaultValue: 'Search processes...' })}
            enableColumnVisibility
            enableColumnFilters
            enableUrlState
            tableId="allProcesses"
          />
        </div>

        {/* Form Dialog */}
        <ProcessFormDialog
          open={formDialogOpen}
          onOpenChange={(open) => !open && closeFormDialog()}
          onSubmit={handleFormSubmit}
          process={editingProcess}
          disableOwnerAndDepartment={
            editingProcess ? !(editingProcess.capabilities?.canEditDepartment ?? false) : false
          }
        />

        {/* Duplicate Dialog */}
        {duplicatingProcess && (
          <DuplicateProcessDialog
            open
            onOpenChange={(open) => !open && setDuplicatingProcess(null)}
            source={duplicatingProcess}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <DeleteConfirmDialog
          open={deleteDialogOpen}
          onOpenChange={(open) => !open && closeDeleteDialog()}
          onConfirm={handleDeleteConfirm}
          isDeleting={isDeleting}
          description={t('processes.deleteConfirmDescription')}
        />
      </PageContent>
    </Page>
  );
}
